import { fetchWrapper } from '~/utils/fetchWrapper'

/** Bundled webapp API contract version — keep in sync with packages/api/routes/api/version.py API_VERSION */
export const CLIENT_API_VERSION = '2.1.0'

export interface VersionMessage {
  level: 'INFO' | 'WARN' | 'ERROR'
  message: string
}

export interface VersionResponse {
  version: string
  min_plugin_version: string
  messages: VersionMessage[]
}

export interface VersionCheckResult {
  isCompatible: boolean
  serverVersion: string
  messages: VersionMessage[]
  /** True when major semver differs (e.g. 2.x vs 3.x) */
  majorVersionMismatch: boolean
  /** True when CLIENT_API_VERSION !== server API_VERSION (full contract string) */
  apiVersionMismatch: boolean
}

export interface PluginVersionCheckOptions {
  /** Semver from bridge.getShellVersion() — sent as shell_version to the API */
  shellVersion?: string | null
}

export class VersionService {
  /**
   * Check bundled webapp API contract vs server API_VERSION, plus server ERROR messages (shell / legacy plugin).
   * Blocks when versions differ by any semver segment or when the server returns ERROR.
   * On network failure, fails open (isCompatible true) so outages do not brick the IDE.
   */
  async checkVersion(options?: PluginVersionCheckOptions): Promise<VersionCheckResult> {
    try {
      const params = new URLSearchParams()
      const shell = options?.shellVersion
      if (shell) {
        params.set('shell_version', shell)
      }
      const qs = params.toString()
      const path = qs ? `/api/version?${qs}` : '/api/version'

      const data = await fetchWrapper.get<VersionResponse>(path, { suppressErrorToast: true })
      const serverVersionRaw = (data.version ?? '').trim()
      const { majorVersionMismatch } =
        serverVersionRaw.length > 0
          ? this.compareMajorOnly(CLIENT_API_VERSION, serverVersionRaw)
          : { majorVersionMismatch: false }
      const apiVersionMismatch =
        serverVersionRaw.length === 0 || CLIENT_API_VERSION !== serverVersionRaw

      const serverErrors = data.messages.filter(m => m.level === 'ERROR')
      const blockingMessages: VersionMessage[] = [...serverErrors]

      if (apiVersionMismatch) {
        blockingMessages.push({
          level: 'ERROR',
          message: `This Curie UI (v${CLIENT_API_VERSION}) does not match the server API (v${serverVersionRaw || 'unknown'}). Update the IDE plugin or try again after the server finishes deploying.`
        })
      }

      const shouldBlock = blockingMessages.length > 0

      console.info(
        `Version check: client=${CLIENT_API_VERSION}, server=${serverVersionRaw}, shell=${shell ?? 'n/a'}, apiMismatch=${apiVersionMismatch}, majorMismatch=${majorVersionMismatch}, serverErrors=${serverErrors.length}, blocked=${shouldBlock}`
      )

      return {
        isCompatible: !shouldBlock,
        serverVersion: serverVersionRaw || data.version,
        messages: shouldBlock ? blockingMessages : [],
        majorVersionMismatch,
        apiVersionMismatch
      }
    } catch (error) {
      console.error('Failed to check API version', error)
      return {
        isCompatible: true,
        serverVersion: 'unknown',
        messages: [],
        majorVersionMismatch: false,
        apiVersionMismatch: false
      }
    }
  }

  private compareMajorOnly(
    clientVersion: string,
    serverVersion: string
  ): { majorVersionMismatch: boolean } {
    try {
      const clientMajor = this.getMajorVersion(clientVersion)
      const serverMajor = this.getMajorVersion(serverVersion)
      return { majorVersionMismatch: clientMajor !== serverMajor }
    } catch (error) {
      console.warn('Failed to parse version', error)
      return { majorVersionMismatch: false }
    }
  }

  private getMajorVersion(version: string): number {
    const match = version.match(/^(\d+)\./)
    if (!match) throw new Error(`Invalid semver format: ${version}`)
    return parseInt(match[1], 10)
  }
}
